// src/game-system/races/baseRace.js

/**
 * @typedef {Object} RacialTrait
 * @property {string} name - Name of the trait
 * @property {string} description - Description of the trait
 * @property {Object} effect - Effect of the trait
 * @property {('passive'|'active')} effect.type - Type of effect
 * @property {string} effect.mechanics - Mechanical description
 * @property {string} [effect.limitations] - Any limitations on the effect
 */

/**
 * @typedef {Object} CulturalBackground
 * @property {string} name - Name of the culture
 * @property {string} description - Description of the culture
 * @property {string[]} bonusSkills - Additional skills granted
 * @property {string[]} startingEquipment - Starting gear
 * @property {Object[]} traditions - Cultural traditions
 */

/**
 * Base race class that all races should extend
 */
export class BaseRace {
    /**
     * @param {Object} config - Race configuration
     * @param {string} config.name - Race name
     * @param {string} config.description - Race description
     * @param {Object.<string, number>} config.attributeBonuses - Attribute modifications
     * @param {RacialTrait[]} config.traits - Racial traits
     * @param {CulturalBackground[]} config.cultures - Available cultures
     */
    constructor(config) {
      this.name = config.name;
      this.description = config.description;
      this.attributeBonuses = config.attributeBonuses;
      this.traits = config.traits;
      this.cultures = config.cultures;
      this.size = config.size;
      this.speed = config.speed;
      this.languages = config.languages;
    }
  
    /**
     * Get racial bonuses for a specific attribute
     * @param {string} attribute - Attribute name
     * @returns {number} Bonus value
     */
    getAttributeBonus(attribute) {
      return this.attributeBonuses[attribute] || 0;
    }
  
    /**
     * Get all traits for this race
     * @returns {RacialTrait[]} Array of racial traits
     */
    getTraits() {
      return this.traits;
    }
  
    /**
     * Get available cultures for this race
     * @returns {CulturalBackground[]} Array of cultures
     */
    getCultures() {
      return this.cultures;
    }
  }